import * as React from 'react';
import { useEffect } from 'react';
import styled from '@emotion/styled';
import { DeveloperModel } from '@journeyapps-platform/lib-journey-core';
import { AccessColumnEvent } from '../../../widgets/table/TableWidget';
import { RowButtonContainer } from '../../../widgets/table/RowButtonWidget';
import { ButtonIconPosition, ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';
import { TooltipPosition } from '../../../utils/Tooltips';
import { useSystem } from '../../../hooks/useSystem';
import { useNavigate, useParams } from 'react-router-dom';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useAllowed } from '../../../hooks/useAllowed';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { Routes } from '../../../routes';
import { DevRow } from './DeveloperTableWidget';
import { Meta } from '../manage-integrations/IntegrationsPage';
import { generatePath } from '../../../utils/paths';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';

export type DeveloperRowEvent = AccessColumnEvent<DevRow<DeveloperModel>>;

export interface DevRowWidgetProps {
  event: DeveloperRowEvent;
}

function generateDeveloperPills(developer: DeveloperModel) {
  const { signup_complete } = developer.model;
  return <S.PillContainer>{!signup_complete && <Meta label="Invite" value="Pending" />}</S.PillContainer>;
}

export const DeveloperRowWidget: React.FC<DevRowWidgetProps> = (props) => {
  const dialogStore = useDialogStore();
  const system = useSystem();
  const orgStore = useOrgStore();
  const navigate = useNavigate();
  const params = useParams<{ org_id: string }>();

  const { event } = props;
  const { row } = event;

  const isAllowedToDeleteDev = useAllowed({ action: UserModelAction.DELETE, resource: event.row.dev });
  const isAllowedToUpdateDev = useAllowed({ action: UserModelAction.UPDATE, resource: event.row.dev });

  const deleteDeveloper = React.useCallback(async () => {
    const ok = await dialogStore.confirmDialog({
      title: 'Please confirm',
      message: `Are you sure you want to delete: ${row.cells.email}? This will permanently disable their entire account.`
    });
    if (!ok) {
      return;
    }
    try {
      await system.accountV5Client.deleteUser({ id: row.key });
      orgStore.current_org?.loadDevelopers();
    } catch (e) {
      console.error(`Failed to delete developer with id:${row.key}`, e);
    }
  }, [row.key]);

  return (
    <S.JustifiedRowButtonContainer>
      {generateDeveloperPills(row.dev)}
      {event.active && (
        <S.RowButtons>
          <ButtonWidget
            icon={'trash'}
            disabled={!isAllowedToDeleteDev}
            tooltip={isAllowedToDeleteDev ? 'Delete developer' : 'Not permitted'}
            type={ButtonType.SECONDARY}
            tooltipPos={TooltipPosition.BOTTOM}
            action={deleteDeveloper}
          />
          <ButtonWidget
            key={`${row.key}-button`}
            disabled={!isAllowedToUpdateDev}
            type={ButtonType.SECONDARY}
            icon={'user'}
            iconPosition={ButtonIconPosition.RIGHT}
            label={'Edit'}
            tooltip={isAllowedToUpdateDev ? 'Edit developer' : 'Not permitted'}
            tooltipPos={TooltipPosition.BOTTOM}
            action={() => {
              navigate(generatePath(Routes.EDIT_USER, { ...params, user_id: row.key }), {
                replace: true
              });
            }}
          />
        </S.RowButtons>
      )}
    </S.JustifiedRowButtonContainer>
  );
};

namespace S {
  export const RowButtons = styled.div`
    display: flex;
    flex-direction: row;
    column-gap: 5px;
  `;

  export const JustifiedRowButtonContainer = styled(RowButtonContainer)`
    width: 300px;
    justify-content: space-between;
    height: 35px;
  `;

  export const PillContainer = styled.div`
    margin-right: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
  `;
}
